import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Bell } from 'lucide-react'
import { useNotifications } from '../../contexts/NotificationContext'
import { isUnread } from '../../services/notifications'
import { notificationHref } from '../../utils/notificationLinks'
import { notificationSections } from '../../utils/notificationPresentation'
import NotificationItemContent from './NotificationItemContent'

/**
 * Header notification bell with unread badge and recent dropdown.
 */
const NotificationBell: React.FC = () => {
  const { unreadCount, recent, loading, markRead, refresh } = useNotifications()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  async function handleOpen() {
    const next = !open
    setOpen(next)
    if (next) await refresh()
  }

  async function handleItemClick(id: number, unread: boolean, href: string | null) {
    if (unread) {
      await markRead(id)
    }
    setOpen(false)
    if (href) navigate(href)
  }

  const sections = notificationSections(recent)

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => void handleOpen()}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={
          unreadCount > 0
            ? `Notifikationer, ${unreadCount} ulæste`
            : 'Notifikationer'
        }
        className="relative inline-flex items-center justify-center p-1.5 text-ink-500 hover:text-ink transition-colors"
      >
        <Bell className="h-4 w-4" aria-hidden />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-0.5 rounded-full bg-signal text-paper text-[10px] font-mono font-semibold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Seneste notifikationer"
          className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-paper border border-rule shadow-lg rounded-sm z-50"
        >
          <div className="px-3 py-2 border-b border-rule flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              Notifikationer
              {unreadCount > 0 && (
                <span className="ml-1.5 font-mono font-normal text-signal">
                  {unreadCount} ulæst{unreadCount === 1 ? '' : 'e'}
                </span>
              )}
            </span>
            <Link
              to="/account/notifikationer/indbakke"
              onClick={() => setOpen(false)}
              className="text-xs text-signal hover:text-signal-700"
            >
              Se alle
            </Link>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading && recent.length === 0 && (
              <p className="px-3 py-4 text-sm text-ink-500">Henter…</p>
            )}
            {!loading && recent.length === 0 && (
              <p className="px-3 py-4 text-sm text-ink-500">Ingen notifikationer endnu.</p>
            )}
            <ul>
              {sections.map((section) => (
                <Fragment key={section.id}>
                  {sections.length > 1 && (
                    <li
                      className="px-3 py-1.5 bg-paper-200/30 border-b border-rule"
                      aria-hidden
                    >
                      <span className="font-mono text-[10px] uppercase tracking-widest text-ink-400">
                        {section.label}
                      </span>
                    </li>
                  )}
                  {section.items.map((n) => {
                    const href = notificationHref(n)
                    const unread = isUnread(n)
                    return (
                      <li key={n.id} className="border-b border-rule last:border-0">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => void handleItemClick(n.id, unread, href)}
                          className="w-full text-left px-3 py-2.5 hover:bg-paper-200/50 transition-colors"
                        >
                          <NotificationItemContent notification={n} compact />
                        </button>
                      </li>
                    )
                  })}
                </Fragment>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
